import { useEffect } from "react";

interface AssetsLoaderProps {
  styles?: string[];
  scripts?: string[];
}

// Fonction pour normaliser les chemins (ajouter %PUBLIC_URL% si nécessaire)
const normalizePath = (path: string): string => {
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return path;
  }
  // Si le chemin ne commence pas par /, on l'ajoute
  if (!path.startsWith('/')) {
    return `/${path}`;
  }
  return path;
};

// Fonction pour charger un script et retourner une Promise
const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const normalizedSrc = normalizePath(src);
    
    // Vérifier si le script est déjà chargé (avec le chemin normalisé)
    const existingScript = document.querySelector(`script[src="${normalizedSrc}"]`) || 
                          document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      console.log(`ℹ️ Script déjà chargé: ${src}`);
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = normalizedSrc;
    script.async = false;
    script.defer = false;
    
    script.onload = () => {
      console.log(`✅ Script chargé: ${src}`);
      resolve();
    };
    
    script.onerror = () => {
      console.error(`❌ Erreur lors du chargement du script: ${src}`);
      // Ne pas rejeter pour permettre aux autres scripts de se charger
      // Mais logger l'erreur pour le débogage
      console.error(`Chemin tenté: ${normalizedSrc}`);
      resolve(); // Résoudre au lieu de rejeter pour continuer
    };
    
    // S'assurer que body existe avant d'ajouter le script
    if (document.body) {
      document.body.appendChild(script);
    } else {
      // Attendre que body soit disponible
      const checkBody = setInterval(() => {
        if (document.body) {
          clearInterval(checkBody);
          document.body.appendChild(script);
        }
      }, 50);
      
      // Timeout de sécurité
      setTimeout(() => {
        clearInterval(checkBody);
        if (!document.body) {
          console.error(`❌ Body non disponible pour charger: ${src}`);
          resolve();
        }
      }, 5000);
    }
  });
};

// Fonction pour charger un style et retourner une Promise
const loadStyle = (href: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const normalizedHref = normalizePath(href);
    
    // Vérifier si le style est déjà chargé (avec le chemin normalisé)
    const existingLink = document.querySelector(`link[href="${normalizedHref}"]`) || 
                        document.querySelector(`link[href="${href}"]`);
    if (existingLink) {
      console.log(`ℹ️ Style déjà chargé: ${href}`);
      resolve();
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = normalizedHref;
    
    link.onload = () => {
      console.log(`✅ Style chargé: ${href}`);
      resolve();
    };
    
    link.onerror = () => {
      console.error(`❌ Erreur lors du chargement du style: ${href}`);
      console.error(`Chemin tenté: ${normalizedHref}`);
      // Ne pas rejeter pour les styles, continuer quand même
      resolve();
    };
    
    if (document.head) {
      document.head.appendChild(link);
    } else {
      // Attendre que head soit disponible
      const checkHead = setInterval(() => {
        if (document.head) {
          clearInterval(checkHead);
          document.head.appendChild(link);
        }
      }, 50);
      
      // Timeout de sécurité
      setTimeout(() => {
        clearInterval(checkHead);
        if (!document.head) {
          console.error(`❌ Head non disponible pour charger: ${href}`);
        }
        resolve();
      }, 5000);
    }
    
    // Timeout pour les styles qui ne déclenchent pas onload
    setTimeout(() => resolve(), 100);
  });
};

// Fonction pour charger les scripts séquentiellement
const loadScriptsSequentially = async (scripts: string[]): Promise<void> => {
  for (const script of scripts) {
    try {
      await loadScript(script);
      // Petit délai entre chaque script pour garantir l'ordre
      await new Promise(resolve => setTimeout(resolve, 50));
    } catch (error) {
      console.error(`Erreur lors du chargement de ${script}:`, error);
      // Continuer avec les autres scripts même en cas d'erreur
    }
  }
};

const AssetsLoader: React.FC<AssetsLoaderProps> = ({ styles = [], scripts = [] }) => {
  useEffect(() => {
    console.log("🔄 Début du chargement des assets...");

    const loadedLinks: HTMLLinkElement[] = [];
    const loadedScripts: HTMLScriptElement[] = [];

    // Charger tous les styles en parallèle
    const loadStyles = async () => {
      const stylePromises = styles.map(async (href) => {
        try {
          await loadStyle(href);
          const normalizedHref = normalizePath(href);
          const link = document.querySelector(`link[href="${normalizedHref}"]`) as HTMLLinkElement;
          if (link) loadedLinks.push(link);
        } catch (error) {
          console.error(`Erreur style ${href}:`, error);
        }
      });
      await Promise.all(stylePromises);
    };

    // Fonction pour initialiser/réinitialiser AOS
    const initializeAOS = () => {
      if (typeof (window as any).AOS !== 'undefined') {
        try {
          // Réinitialiser AOS pour détecter les nouveaux éléments
          (window as any).AOS.refreshHard();
          console.log("✅ AOS réinitialisé");
        } catch (error) {
          console.error("❌ Erreur lors de la réinitialisation de AOS:", error);
        }
      }
    };

    // Charger les scripts séquentiellement
    const loadAllScripts = async () => {
      await loadScriptsSequentially(scripts);
      
      // Après le chargement de tous les scripts, initialiser AOS si disponible
      if (scripts.some(s => s.includes('aos.js'))) {
        // Attendre que le DOM soit prêt
        const waitForDOM = () => {
          if (document.readyState === 'complete' || document.readyState === 'interactive') {
            setTimeout(() => {
              if (typeof (window as any).AOS !== 'undefined') {
                try {
                  (window as any).AOS.init({
                    duration: 600,
                    easing: 'ease-in-out',
                    once: true,
                    mirror: false
                  });
                  console.log("✅ AOS initialisé");
                  
                  // Réinitialiser après un court délai pour capturer les éléments React
                  setTimeout(initializeAOS, 500);
                } catch (error) {
                  console.error("❌ Erreur lors de l'initialisation de AOS:", error);
                }
              }
            }, 200);
          } else {
            document.addEventListener('DOMContentLoaded', waitForDOM);
          }
        };
        waitForDOM();
      }
    };

    // Observer les changements du DOM pour réinitialiser AOS quand de nouveaux éléments sont ajoutés
    let observer: MutationObserver | null = null;
    if (scripts.some(s => s.includes('aos.js'))) {
      observer = new MutationObserver(() => {
        setTimeout(initializeAOS, 300);
      });
      
      // Démarrer l'observation une fois que le body existe
      const startObserving = () => {
        if (document.body && observer) {
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        } else {
          setTimeout(startObserving, 100);
        }
      };
      startObserving();
    }

    // Exécuter le chargement
    const initializeAssets = async () => {
      try {
        // Charger les styles d'abord
        await loadStyles();
        console.log("✅ Tous les styles sont chargés");
        
        // Ensuite charger les scripts
        await loadAllScripts();
        console.log("✅ Tous les scripts sont chargés");
        
        // Attendre que le DOM soit prêt
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            console.log("✅ DOM prêt");
            // Réinitialiser AOS après que React ait rendu
            setTimeout(initializeAOS, 1000);
          });
        } else {
          console.log("✅ DOM déjà prêt");
          // Réinitialiser AOS après que React ait rendu
          setTimeout(initializeAOS, 1000);
        }
      } catch (error) {
        console.error("❌ Erreur lors du chargement des assets:", error);
      }
    };

    initializeAssets();

    // Nettoyage lors du démontage
    return () => {
      if (observer) {
        observer.disconnect();
      }
      loadedLinks.forEach((link) => {
        if (link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });
      loadedScripts.forEach((script) => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, [styles, scripts]);

  return null;
};

export default AssetsLoader;
